import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { CheckMarkIcon, CreditCardIcon, EmailIcon, PenIcon, UploadIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { blockMissionTrackingPas } from '../../api/mission-trackings/block-mission-tracking-pas'
import {
  getMissionTrackingTotalSalariesWithoutPas,
  getMissionTrackingTotalSalariesWithPas
} from '../../api/mission-trackings/get-mission-tracking-salary-total'
import { sendEmailToMissionTrackings } from '../../api/mission-trackings/send-email'
import { unblockMissionTrackingPas } from '../../api/mission-trackings/unblock-mission-tracking-pas'
import { updateMissionTrackingPas } from '../../api/mission-trackings/update-mission-tracking-pas'
import { validateMissionTrackingsSalaries } from '../../api/mission-trackings/validate-mission-tracking-salary-total'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import { getMissionTrackingSalaryColumns } from '../../components/pages/mission-trackings/mission-tracking-salary.utils'
import { MissionTrackingSalaryFilters } from '../../components/pages/mission-trackings/mission-tracking-salary-filters'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { StaffPaymentStatusEnum } from '../../lib/types/enums/affected-staff/staff-payment-status.enum'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import {
  MissionToStaffSalaryFilter,
  MissionTrackingSalary
} from '../../lib/types/models/mission-tracking/mission-tracking'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformMissionToStaffSalaryFiltersToUrl,
  transformUrlToMissionToStaffSalaryFilters
} from '../../lib/utils/filters-mapping/mission-to-staff-salary'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

export function MissionTrackingsSalariesListPage() {
  const { t } = useTranslate()
  const { navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = useRef<DynamicTableRef<MissionTrackingSalary>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'send-email',
    MissionTrackingSalary[]
  >(['filters', 'send-email'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<MissionToStaffSalaryFilter>({
      // Validated transfers are hidden by default, they stay reachable through the filters
      initialFilters: {
        paymentStatuses: [StaffPaymentStatusEnum.PENDING, StaffPaymentStatusEnum.EXPORTED]
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformMissionToStaffSalaryFiltersToUrl,
      transformUrlToFilter: transformUrlToMissionToStaffSalaryFilters
    })

  const { mutate: blockMissionTrackingPasMutation } = useMutation({
    mutationFn: blockMissionTrackingPas,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Le PAS a été bloqué avec succès')
      tableRef.current?.refetch()
    }
  })

  const { mutate: unblockMissionTrackingPasMutation } = useMutation({
    mutationFn: unblockMissionTrackingPas,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Le PAS a été débloqué avec succès')
      tableRef.current?.refetch()
    }
  })

  const sendEmail = useMutation({
    mutationFn: sendEmailToMissionTrackings,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const { mutate: updateMissionTrackingPasMutation } = useMutation({
    mutationFn: updateMissionTrackingPas,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Le PAS a été mis à jour avec succès')
      tableRef.current?.refetch()
    }
  })

  const { mutateAsync: validateMissionTrackingsSalariesMutation } = useMutation({
    mutationFn: validateMissionTrackingsSalaries,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Les salaires ont été validés avec succès')
      tableRef.current?.refetch()
    }
  })

  const { data: totalSalariesWithPas } = useQuery({
    queryKey: ['mission-tracking-salaries-total-with-pas', JSON.stringify(selectedFilter)],
    queryFn: () => getMissionTrackingTotalSalariesWithPas(selectedFilter ?? {})
  })

  const { data: totalSalariesWithoutPas } = useQuery({
    queryKey: ['mission-tracking-salaries-total-without-pas', JSON.stringify(selectedFilter)],
    queryFn: () => getMissionTrackingTotalSalariesWithoutPas(selectedFilter ?? {})
  })

  const handlePasBlockedChange = (checked: boolean, missionTracking: MissionTrackingSalary) => {
    if (checked) blockMissionTrackingPasMutation(missionTracking.id)
    else unblockMissionTrackingPasMutation(missionTracking.id)
  }

  const handlePasChange = (amount: number | null, missionTracking: MissionTrackingSalary) => {
    updateMissionTrackingPasMutation({ id: missionTracking.id, amount })
  }

  const getAvailableMissionTrackingIds = (missionTrackings: MissionTrackingSalary[]) => {
    return missionTrackings.filter((mt) => mt.pasBlocked).map((mt) => mt.id)
  }

  return (
    <>
      <UserNavBar title={'Paiement et suivi des honoraires AAT'} icon={CreditCardIcon} />
      <div className="h-4" />

      <DynamicDataTable<MissionTrackingSalary, MissionToStaffSalaryFilter>
        onLoadingChange={setIsTableLoading}
        ref={tableRef}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        title={'Paiement AAT'}
        selectable
        disableRow={({ pasBlocked }) => !pasBlocked}
        columns={getMissionTrackingSalaryColumns({
          t,
          role: RoleEnum.ADMIN,
          handlePasBlockedChange,
          handlePasChange
        })}
        allowSearchParam
        onFiltersClick={() => toggleModal('filters')}
        endpoint={API.missionTrackings.getMissionTrackingsSalaries.url}
        onRowClick={(mt, event) =>
          navigateWithEvent(
            Paths.MISSION_TRACKING_UPDATE_PAGE.replace(':id', mt.id.toString()),
            event
          )
        }
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            icon: UploadIcon,
            label: 'Export excel des salaires avant PAS',
            variant: 'ghost',
            fn: async (missionTrackings) => {
              const ids = getAvailableMissionTrackingIds(missionTrackings)
              if (ids.length === 0) {
                toast.error("Aucun des suivis de mission sélectionnés n'a le PAS bloqué.")
                return
              }
              await downloadDocuments({
                ids,
                endpoint: API.missionTrackings.exportMissionTrackingsSalariesBeforePas.url
              })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'multiple',
            icon: PenIcon,
            label: 'Editer les virements',
            variant: 'ghost',
            fn: async (missionTrackings) => {
              //if pas == null or 0, show error toast
              const message =
                missionTrackings.length === 1
                  ? 'Le PAS n’a pas encore été saisi, édition du virement impossible.'
                  : 'Le PAS de certains encadrants n’a pas encore été saisi, édition des virements impossible.'

              const hasPas = missionTrackings.some((as) => as.pas)
              if (!hasPas) {
                toast.error(message)
                return
              }
              const ids = getAvailableMissionTrackingIds(missionTrackings)
              if (ids.length === 0) {
                toast.error("Aucun des suivis de mission sélectionnés n'a le PAS bloqué.")
                return
              }
              await downloadDocuments({
                ids,
                endpoint: API.missionTrackings.exportMissionTrackingsSalariesToXml.url
              })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'multiple',
            icon: CheckMarkIcon,
            label: 'Virement validé',
            variant: 'ghost',
            fn: async (missionTrackings) => {
              const ids = getAvailableMissionTrackingIds(missionTrackings)
              if (ids.length === 0) return
              if (
                missionTrackings.some((mt) => mt.paymentStatus === StaffPaymentStatusEnum.PENDING)
              ) {
                toast.error(
                  'Tous les éléments sélectionnés doivent être au statut « Fichier de virement exporté ».'
                )
                return
              }
              await validateMissionTrackingsSalariesMutation(ids)
            }
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'Email groupé',
            variant: 'ghost',
            fn: (mts) => {
              const ids = getAvailableMissionTrackingIds(mts)
              if (ids.length === 0) {
                toast.error("Aucun des suivis de mission sélectionnés n'a le PAS bloqué.")
                return
              }
              const filteredMts = mts
                .filter(
                  (mt, index, self) =>
                    index === self.findIndex((t) => t.staff.user.email === mt.staff.user.email)
                )
                .filter((mt) => ids.includes(mt.id))
              toggleModal('send-email', filteredMts)
            }
          }
        ]}
        footerclassName="bg-primary-light"
        footerCells={[
          {
            accessorKey: 'id',
            value: (
              <Typography variant="caption" color={tailwindColors.primary.DEFAULT}>
                Total
              </Typography>
            )
          },
          {
            accessorKey: 'viewData.totalSalary',
            value: (
              <Badge
                variant="orange"
                text={NumberFormatter.formatCurrency(totalSalariesWithoutPas?.total ?? 0)}
              />
            )
          },
          {
            accessorKey: 'viewData.totalSalaryAfterPas',
            value: (
              <Badge
                variant="orange"
                text={NumberFormatter.formatCurrency(totalSalariesWithPas?.total ?? 0)}
              />
            )
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <MissionTrackingSalaryFilters filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <Modal
          hideActions
          size="medium"
          icon={EmailIcon}
          title={'Envoyer un email'}
          isOpen={modalState['send-email']}
          onCancel={() => closeModel('send-email')}
        >
          <SendEmailForm
            className="modal-body"
            defaultValues={{
              emails: selectedData
                .filter(
                  (mt, index, self) =>
                    index === self.findIndex((t) => t.staff.user.email === mt.staff.user.email)
                )
                .map((row) => row.staff.user.email),
              subject: '',
              body: ''
            }}
            labels={selectedData.map(
              (mt) =>
                `${toFullName(mt.staff.firstName, mt.staff.lastName)} - ${mt.staff.user.email}`
            )}
            buttonsConfig={modalButtonConfig({
              isLoading: sendEmail.isPending,
              onCancel: () => closeModel('send-email'),
              onConfirm: (v) =>
                sendEmail.mutate({
                  ...v,
                  emails: selectedData.map((mt) => mt.staff.user.email)
                })
            })}
          />
        </Modal>
      )}
    </>
  )
}
